import { LoaderScene } from './LoaderScene';
import WebFont from 'webfontloader';
import { getDeviceMetric, PERFORMANCE_CATEGORY } from '../../utils/deviceDetection';

// =============================================================================================
// Loader plugin
// =============================================================================================

export class LoaderPlugin extends Phaser.Plugins.BasePlugin {
  constructor(pluginManager) {
    super(pluginManager);
    this._autoshow = false;
    this._displayScene = LoaderScene;
    this._displaySceneConfig = false;
    this.game.plugins.installScenePlugin('LoaderScenePlugin', LoaderScenePlugin);
  }

  init(config) {
    this.config = config;
    this._displayScene = this.config?.displayScene || this._displayScene;
    this._displaySceneConfig = this.config?.displayConfig || this._displaySceneConfig;
  }

  get scene() {
    if (!this._scene) {
      this.game.scene.add('jggloader', this._displayScene, true, {
        displayConfig: this._displaySceneConfig,
      });
      this._scene = this.game.scene.getScene('jggloader');
    }
    return this._scene;
  }

  get autoshow() {
    return this._autoshow;
  }

  setDisplayScene(displayScene, displayConfig) {
    this._displayScene = displayScene || this._displayScene;
    this._displaySceneConfig = displayConfig || this._displaySceneConfig;
  }

  bootGame(config, onComplete, paths) {
    this.scene.load.setBaseURL(this.game.appUrls.getBaseDirectory());
    _loadConfig(this.scene, config.boot, () => {
      this.scene.load.setBaseURL(this.game.appUrls.getBaseDirectory());
      this._autoshow = true;
      this.scene.scene.setVisible(this._autoshow);
      this.loadConfig(config.load, () => {
        if (onComplete) onComplete();
      });
    });
  }

  loadAssetPack(pack, keys, onComplete) {
    this.scene.scene.setVisible(this._autoshow);
    this.scene.load.setBaseURL(this.game.appUrls.getBaseDirectory());
    const config = { assetpacks: [{ config: pack, keys }] };
    this.loadConfig(config, () => {
      if (onComplete) onComplete();
    });
  }

  loadConfig(config, onComplete) {
    _loadConfig(this.scene, config, onComplete);
  }
}

const _loadConfig = (scene, config, onComplete) => {
  let _assetsLoaded = false;
  let _fontsLoaded = false;

  const _checkLoad = () => {
    if (_assetsLoaded && _fontsLoaded && onComplete) {
      onComplete();
    }
  };
  const _handleLoadComplete = () => {
    _assetsLoaded = true;
    _checkLoad();
  };
  const _handleFontsComplete = () => {
    _fontsLoaded = true;
    _checkLoad();
  };

  loadFonts(scene, config.fonts, _handleFontsComplete);

  config.assetpacks?.forEach((pack) => {
    pack.keys.forEach((key) => {
      if (scene.game.isLowEndDevice && pack.config[key].pathLow) {
        console.log('Loading low-end device assets for', key);
        pack.config[key].path = pack.config[key].pathLow;
      }
      scene.load.addPack(pack.config, key);
    });
  });
  scene.load.once('complete', _handleLoadComplete, this);
  scene.load.start();
};

const loadFonts = (scene, fonts, onComplete) => {
  if (!fonts || fonts.length === 0) {
    onComplete && onComplete();
    return;
  }
  const loadFonts = fonts.reduce(
    (obj, font) => {
      for (const i in font) {
        if (i === 'CSS') {
          obj.urls.push(
            ...font[i].map((css) => {
              return scene.game.appUrls.getFontsDirectory(css);
            })
          );
        } else {
          obj.families.push(font[i]);
        }
      }
      return obj;
    },
    { families: [], urls: [] }
  );
  WebFont.load({
    custom: loadFonts,
    active: onComplete,
    inactive: onComplete,
  });
};

// =============================================================================================
// We need a scene plugin to listen for the events and tell the master plugin what to do
// =============================================================================================

class LoaderScenePlugin extends Phaser.Plugins.ScenePlugin {
  constructor(scene, pluginManager) {
    super(scene, pluginManager);
    scene.load.on('start', (loader) => {
      if (this.game.loader.autoshow && loader.list.size > 0) {
        this.game.loader.scene.scene.bringToTop();
      }
    });
    scene.load.on('progress', (value) => {
      if (this.game.loader.autoshow) {
        this.game.loader.scene.setProgress(value);
      }
    });
    scene.load.on('complete', () => {
      if (this.game.loader.autoshow) {
        this.game.loader.scene.scene.sendToBack();
      }
    });
  }
}
